import {
	alias,
	date,
	deserialize,
	identifier,
	list,
	object,
	serializable,
} from "serializr";
import { IJsonObject } from "../api";
import { Attachment } from "../attachments";
import { Opportunity } from "../opportunities";
import { User } from "../users";
import { Project } from "./Project";
import { ProjectMember } from "./ProjectMember";
import { ProjectTaskAction } from "./ProjectTaskAction";
import { ProjectTaskChecklist } from "./ProjectTaskChecklist";
import { ProjectTaskComment } from "./ProjectTaskComment";
import { ProjectTaskLabel } from "./ProjectTaskLabel";
import { ProjectTaskTimelog } from "./ProjectTaskTimelog";

interface IProps {
	id?: number | null;
	projectId?: number | null;
	opportunityId?: number | null;
	name?: string;
	project?: Project | null;
	description?: string;
	estimatedHours?: number;
	status?: string;
	startDate?: Date | null;
	completedDate?: Date | null;
	dueDate?: Date | null;
	position?: number | null;
	createdDate?: Date;
	members?: ProjectMember[];
	timelogs?: ProjectTaskTimelog[];
	actions?: ProjectTaskAction[];
	comments?: ProjectTaskComment[];
	checklists?: ProjectTaskChecklist[];
	opportunity?: Opportunity | null;
	labels?: ProjectTaskLabel[];
	watchers?: User[];
	attachments?: Attachment[];
	attachmentBaseUrl?: string;
}

export enum ProjectTaskStatus {
	NotStarted = "Not Started",
	InProgress = "In Progress",
	OnHold = "On Hold",
	InternalReview = "Internal Review",
	ClientReview = "Client Review",
	Completed = "Completed",
	Live = "Live",
	Trash = "Trash",
}

export const getColorForTaskStatus = (status: string) => {
	if (status === ProjectTaskStatus.NotStarted) {
		return "#eec300";
	} else if (status === ProjectTaskStatus.InProgress) {
		return "#62d26f";
	} else if (status === ProjectTaskStatus.OnHold) {
		return "#20aaea";
	} else if (status === ProjectTaskStatus.InternalReview) {
		return "#7a6ff0";
	} else if (status === ProjectTaskStatus.ClientReview) {
		return "#aa62e3";
	} else if (status === ProjectTaskStatus.Completed) {
		return "#ea4e9d";
	} else if (status === ProjectTaskStatus.Live) {
		return "#6558ee";
	} else if (status === ProjectTaskStatus.Trash) {
		return "#7f979b";
	}

	return "";
};

export class ProjectTask {
	get getLink() {
		return `/projects/${this.projectId}/${this.id}`;
	}
	public static fromJson(json: IJsonObject): ProjectTask {
		return deserialize(ProjectTask, json);
	}

	public static fromJsonArray(json: IJsonObject[]): ProjectTask[] {
		return json.map((task: IJsonObject) => this.fromJson(task));
	}

	@serializable(identifier())
	public id: number | null;

	@serializable(alias("project_id"))
	public projectId: number | null;

	@serializable(alias("opportunity_id"))
	public opportunityId: number | null;

	@serializable(object(Project))
	public project: Project | null;

	@serializable
	public position: number | null;

	@serializable
	public name: string;

	@serializable
	public description: string;

	@serializable
	public status: string;

	@serializable(alias("estimated_hours"))
	public estimatedHours: number;

	@serializable(alias("start_date", date()))
	public startDate: Date | null;

	@serializable(alias("due_date", date()))
	public dueDate: Date | null;

	@serializable(alias("completed_date"))
	public completedDate: Date | null;

	@serializable(alias("created_at"))
	public createdDate: Date;

	@serializable(list(object(ProjectMember)))
	public members: ProjectMember[];

	@serializable(list(object(ProjectTaskTimelog)))
	public timelogs: ProjectTaskTimelog[];

	@serializable(list(object(ProjectTaskAction)))
	public actions: ProjectTaskAction[];

	@serializable(list(object(ProjectTaskComment)))
	public comments: ProjectTaskComment[];

	@serializable(list(object(ProjectTaskChecklist)))
	public checklists: ProjectTaskChecklist[];

	@serializable(object(Opportunity))
	public opportunity: Opportunity | null;

	@serializable(list(object(ProjectTaskLabel)))
	public labels: ProjectTaskLabel[];

	@serializable(list(object(User)))
	public watchers: User[];

	@serializable(list(object(Attachment)))
	public attachments: Attachment[];

	@serializable(alias("attachment_base_url"))
	public attachmentBaseUrl: string;

	constructor({
		id = null,
		projectId = null,
		opportunityId = null,
		name = "",
		project = null,
		description = "",
		estimatedHours = 0,
		status = "Not Started",
		startDate = null,
		dueDate = null,
		completedDate = null,
		position = null,
		createdDate = new Date(),
		opportunity = null,
		members = [],
		timelogs = [],
		actions = [],
		comments = [],
		checklists = [],
		labels = [],
		watchers = [],
		attachments = [],
		attachmentBaseUrl = "",
	}: IProps = {}) {
		this.id = id;
		this.projectId = projectId;
		this.opportunityId = opportunityId;
		this.name = name;
		this.description = description;
		this.estimatedHours = estimatedHours;
		this.status = status;
		this.startDate = startDate;
		this.dueDate = dueDate;
		this.completedDate = completedDate;
		this.position = position;
		this.createdDate = createdDate;
		this.members = members;
		this.timelogs = timelogs;
		this.actions = actions;
		this.comments = comments;
		this.project = project;
		this.checklists = checklists;
		this.opportunity = opportunity;
		this.labels = labels;
		this.watchers = watchers;
		this.attachments = attachments;
		this.attachmentBaseUrl = attachmentBaseUrl;
	}

	public isWatchedBy(user: User): boolean {
		const watcher = this.watchers.filter(u => {
			return u.id === user.id;
		});

		return watcher.length > 0;
	}

	public cover(): string | null {
		if (!this.attachments) {
			return null;
		}

		let foundCover = null;

		this.attachments.forEach(attachment => {
			if (attachment.isImage()) {
				foundCover = `/download?file=${encodeURIComponent(
					attachment.filename,
				)}&folder=${this.attachmentBaseUrl}&download=false`;
			}
		});

		return foundCover;
	}
}
